import React, { useEffect, useRef } from 'react'
import './halloween.css'

// Хэллоуинская тема для всего приложения: туман «Котёл» (WebGL-шейдер) на фоне,
// паутина в верхних углах и редкие молнии. Только код, без файлов картинок.
// Включается флагом HALLOWEEN_THEME в src/config/brand.js.

const VERTEX_SHADER = 'attribute vec2 p;void main(){gl_Position=vec4(p,0.,1.);}'
// fbm-шум с domain warping: ядовито-зелёный пар снизу, фиолетовая мгла сверху.
// highp обязателен на мобильных GPU: в mediump хэш на sin() теряет точность и шум пропадает.
const FRAGMENT_SHADER = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
uniform vec2 r;
uniform float t;
float h(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}
float n(vec2 p){vec2 i=floor(p),f=fract(p);f=f*f*(3.-2.*f);
  return mix(mix(h(i),h(i+vec2(1.,0.)),f.x),mix(h(i+vec2(0.,1.)),h(i+vec2(1.,1.)),f.x),f.y);}
float fbm(vec2 p){float v=0.,a=.5;mat2 m=mat2(1.6,1.2,-1.2,1.6);
  for(int i=0;i<5;i++){v+=a*n(p);p=m*p;a*=.5;}return v;}
void main(){
  vec2 uv=gl_FragCoord.xy/r;
  vec2 p=uv*vec2(r.x/r.y,1.)*2.2;
  vec2 q=vec2(fbm(p+vec2(0.,t*.06)),fbm(p+vec2(5.2,1.3)-t*.04));
  vec2 w=vec2(fbm(p+2.*q+vec2(1.7,9.2)+t*.05),fbm(p+2.*q+vec2(8.3,2.8)));
  float f=fbm(p+2.4*w+vec2(t*.03,-t*.08));
  float ground=smoothstep(.85,0.,uv.y);
  vec3 col=mix(vec3(.018,.012,.035),vec3(.05,.02,.075),uv.y);
  col+=vec3(.42,.24,.62)*f*f*(.25+.75*uv.y)*.85;
  col+=vec3(.2,1.,.45)*pow(f,3.2)*ground*1.1;
  col+=vec3(1.,.45,.08)*pow(max(w.x-.45,0.),2.)*ground*.6;
  float vig=smoothstep(1.25,.35,length(uv-vec2(.5,.45)));
  // Дизеринг ±0.5 уровня цвета: убирает ступеньки-полосы на тёмных градиентах после растягивания
  col=col*vig+(h(gl_FragCoord.xy+fract(t))-.5)/255.;
  gl_FragColor=vec4(col,1.);
}`

// Бюджет пикселей холста на телефонах: экран маленький, поэтому выходит почти
// полное разрешение в CSS-пикселях (на desktop — 40% от окна, ~330 тыс. пикселей на Full HD)
const MOBILE_PIXEL_BUDGET = 260000

const getQuality = () => {
  if (window.matchMedia('(min-width: 1024px)').matches) return { scale: 0.4, fps: 30 }
  const area = window.innerWidth * window.innerHeight
  return { scale: Math.min(1, Math.sqrt(MOBILE_PIXEL_BUDGET / area)), fps: 30 }
}

const createRenderer = (canvas) => {
  const gl = canvas.getContext('webgl', { antialias: false, alpha: false, powerPreference: 'low-power' })
  if (!gl) return null
  const compile = (type, source) => {
    const shader = gl.createShader(type)
    gl.shaderSource(shader, source)
    gl.compileShader(shader)
    return shader
  }
  const program = gl.createProgram()
  gl.attachShader(program, compile(gl.VERTEX_SHADER, VERTEX_SHADER))
  gl.attachShader(program, compile(gl.FRAGMENT_SHADER, FRAGMENT_SHADER))
  gl.linkProgram(program)
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null
  gl.useProgram(program)

  const buffer = gl.createBuffer()
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW)
  const position = gl.getAttribLocation(program, 'p')
  gl.enableVertexAttribArray(position)
  gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0)
  const resolution = gl.getUniformLocation(program, 'r')
  const time = gl.getUniformLocation(program, 't')

  return {
    resize: (w, h) => {
      gl.viewport(0, 0, w, h)
      gl.uniform2f(resolution, w, h)
    },
    draw: (seconds) => {
      gl.uniform1f(time, seconds)
      gl.drawArrays(gl.TRIANGLES, 0, 3)
    },
    dispose: () => {
      gl.deleteBuffer(buffer)
      gl.deleteProgram(program)
      gl.getExtension('WEBGL_lose_context')?.loseContext()
    }
  }
}

const CauldronFog = () => {
  const canvasRef = useRef(null)

  useEffect(() => {
    const canvas = canvasRef.current
    const renderer = canvas && createRenderer(canvas)
    if (!renderer) return undefined

    let quality = getQuality()
    const resize = () => {
      quality = getQuality()
      canvas.width = Math.max(1, Math.round(window.innerWidth * quality.scale))
      canvas.height = Math.max(1, Math.round(window.innerHeight * quality.scale))
      renderer.resize(canvas.width, canvas.height)
    }
    resize()
    window.addEventListener('resize', resize)

    // При reduced-motion — один статичный кадр без анимации
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      renderer.draw(8)
      return () => {
        window.removeEventListener('resize', resize)
        renderer.dispose()
      }
    }

    const start = performance.now()
    let raf = 0
    let last = 0
    const tick = (now) => {
      raf = requestAnimationFrame(tick)
      if (document.hidden || now - last < 1000 / quality.fps) return
      last = now
      renderer.draw((now - start) / 1000)
    }
    raf = requestAnimationFrame(tick)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', resize)
      renderer.dispose()
    }
  }, [])

  return (
    <div className="halloween-fog" aria-hidden="true">
      <canvas ref={canvasRef} />
    </div>
  )
}

// Паутина в углу: радиальные нити из угла и провисающие дуги между ними
const WEB_SPOKES = [0, 15, 32, 50, 68, 90]
const WEB_RINGS = [28, 52, 80, 112, 148, 190]
const polar = (radius, degrees) => {
  const angle = (degrees * Math.PI) / 180
  return [radius * Math.cos(angle), radius * Math.sin(angle)]
}
const WEB_PATH = [
  ...WEB_SPOKES.map((angle) => {
    const [x, y] = polar(220, angle)
    return `M0 0 L${x.toFixed(1)} ${y.toFixed(1)}`
  }),
  ...WEB_RINGS.map((radius) => WEB_SPOKES.slice(1).map((angle, index) => {
    const prev = WEB_SPOKES[index]
    const [x1, y1] = polar(radius, prev)
    const [x2, y2] = polar(radius, angle)
    // Контрольная точка ближе к углу — нить провисает
    const [cx, cy] = polar(radius * 0.82, (prev + angle) / 2)
    return `${index === 0 ? `M${x1.toFixed(1)} ${y1.toFixed(1)}` : ''} Q${cx.toFixed(1)} ${cy.toFixed(1)} ${x2.toFixed(1)} ${y2.toFixed(1)}`
  }).join(' '))
].join(' ')

const CornerWeb = () => (
  <svg className="halloween-web" viewBox="0 0 220 220">
    <path d={WEB_PATH} fill="none" stroke="rgba(225, 220, 245, 0.32)" strokeWidth="0.9" />
  </svg>
)

const HalloweenTheme = () => (
  <>
    <CauldronFog />
    <div className="halloween-decor" aria-hidden="true">
      <div className="halloween-lightning" />
      <CornerWeb />
    </div>
  </>
)

export default HalloweenTheme
